import { createRouter, createWebHashHistory } from "vue-router";
import store from ".././store";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import authService from "@/services/auth.service";
import { fingerprint_display } from "@/globals/fpdisplay";
import Subscriptions from "@/views/SubscriptionsOverview.vue";
import { forgetPendingPayment, paymentCancelledRedirect } from "@/store/products.module";

const routes = [
  {
    /* Where Stripe returns after a completed payment — `paymentSuccessUrl()` in
       backend-nextja/src/lib/stripe.ts defaults to app.toplistbot.com/#/success.
       Until now no route matched it, so a paying customer landed on a blank
       page. Kept behind auth deliberately: the page reads the account balance to
       confirm the credits arrived, which needs the session anyway. */
    meta: { title: "Payment complete" },
    path: "/success",
    name: "payment-success",
    component: () => import("@/views/PaymentSuccess.vue"),
  },
  /*
   * The other three places a payment provider sends the customer back to. None
   * of them matched a route, so the catch-all dropped the customer on
   * /dashboard with no word about their payment.
   *
   * `/cancel` — Stripe's cancel_url, `paymentCancelUrl()` in
   * backend-nextja/src/lib/stripe.ts, for one-off and subscription checkouts
   * alike. Back to the step they left, with the package restored and a
   * "nothing was charged" strip; see `paymentCancelledRedirect`.
   */
  { path: "/cancel", redirect: () => paymentCancelledRedirect() },
  /* The billing portal's return_url (`portalReturnUrl()`, same file). */
  { path: "/subscriptions", redirect: "/subscription-plans" },
  /* Heleket's url_success (heleket/src/service.ts). The success page in crypto
     mode, which words the wait for network confirmation. The paid package is
     forgotten here, before the auth guard, so a customer signed out on the way
     back cannot have it put back on the payment page later. */
  {
    path: "/invoices/paid",
    redirect: () => {
      forgetPendingPayment();
      return { path: "/success", query: { method: "crypto" } };
    },
  },
  {
    // Document title tag
    // We combine it with defaultDocumentTitle set in `src/main.js` on router.afterEach hook
    meta: {
      title: "Dashboard",
    },
    path: "/dashboard",
    name: "dashboard",
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Dashboard.vue"),
  },
  {
    // Document title tag
    // We combine it with defaultDocumentTitle set in `src/main.js` on router.afterEach hook
    meta: {
      title: "Chart",
    },
    path: "/logs/chart/:id",
    name: "chart",
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Chart.vue"),
  },
  {
    // Document title tag
    // We combine it with defaultDocumentTitle set in `src/main.js` on router.afterEach hook
    meta: {
      title: "Grouped",
    },
    path: "/logs/grouped/:id",
    name: "grouped",
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/GroupedByNames.vue"),
  },
  {
    // Document title tag
    // We combine it with defaultDocumentTitle set in `src/main.js` on router.afterEach hook
    meta: {
      title: "Dashboard",
    },
    path: "/",
    name: "home",
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Orders.vue"),
  },
  {
    meta: {
      title: "products",
    },
    path: "/products",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Products.vue"),
  },
  {
    path: "/subscription-plans",
    name: "subscriptions-plans",
    component: Subscriptions,
    meta: {
      requiresAuth: true,
      title: "Subscriptions"
    }
  },
  {
    meta: {
      title: "Replenish",
    },
    path: "/replenish",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Replenish.vue"),
  },
  {
    meta: {
      title: "Account Generator Jobs",
    },
    path: "/generation/jobs",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Account-generation/Jobs.vue"),
  },
  {
    meta: {
      title: "Affiliate Center",
    },
    path: "/affiliates/index",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Affiliate/Index.vue"),
  },
  {
    meta: {
      title: "Logs",
    },
    path: "/logs/:id",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Logs.vue"),
  },
  {
    /* The per-campaign chart, reached from the sidebar rather than from a row
       in the job list — so it has no id and owns a campaign picker instead.
       The chosen campaign is mirrored into `?id=`, which on this hash router
       lives inside the fragment and is read via `route.query`.
       Deliberately NOT in `publicPages`: it reads the account's campaigns. */
    meta: {
      title: "Campaign Chart",
    },
    path: "/campaigns/chart",
    name: "campaign-chart",
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/CampaignChart.vue"),
  },
  {
    meta: {
      title: "Campaigns",
    },
    path: "/campaigns",
    name: "campaigns",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Orders.vue"),
  },
  /*
   * Legacy paths. The labels, the headings and the URLs used to disagree —
   * the sidebar said "Vote Jobs", the page said "campaign" and the address bar
   * said "orders" — so the URLs moved to match the word the product actually
   * uses. These keep every existing bookmark, shared link and email link
   * working. A path redirect matches exactly, so the child route needs its own
   * entry; `/orders` alone would not cover `/orders/chart`.
   */
  { path: "/orders", redirect: "/campaigns" },
  { path: "/orders/chart", redirect: "/campaigns/chart" },
  { path: "/profilev2", redirect: "/account" },

  {
    meta: {
      title: "Checkout",
    },
    path: "/checkout",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Checkout.vue"),
  },
  {
    meta: {
      title: "Discord Tokens",
    },
    path: "/discord-tokens",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/DiscordTokens.vue"),
  },
  {
    meta: {
      title: "View websites",
    },
    path: "/websites",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Websites.vue"),
  },
  {
    meta: {
      title: "Website Subscription",
    },
    path: "/websites/:websiteId/subscription",
    name: "WebsiteSubscription",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/WebsiteSubscription.vue"),
  },
  {
    meta: {
      title: "Account",
    },
    path: "/account",
    name: "account",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "tables" */ "@/views/Account.vue"),
  },
  // {
  //   meta: {
  //     title: 'Profile'
  //   },
  //   path: '/profile',
  //   name: 'profile',
  //   component: () => import(/* webpackChunkName: "profile" */ '@/views/Profile.vue')
  // },
  {
    // fullScreen like the other auth screens: the callback signs the user in
    // before it redirects, and without this the navbar, sidebar and their
    // body padding appeared around the "Signing you in…" screen meanwhile.
    meta: {
      fullScreen: true,
    },
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/components/AuthCallback.vue')
  },
  {
    meta: {
      title: "Login",
      fullScreen: true,
    },
    path: "/login",
    name: "login",
    component: () =>
      import(/* webpackChunkName: "login" */ "@/views/Login.vue"),
  },
  {
    meta: {
      title: "Register",
      fullScreen: true,
    },
    path: "/register",
    name: "register",
    component: () =>
      import(/* webpackChunkName: "register" */ "@/views/RegisterV2.vue"),
  },
  {
    /* Password reset, step 1 of 2. Was a server-rendered page on the backend;
       moved into the panel so it uses the real components, logo and theme.
       `fullScreen` matches /login and /register — these pages draw their own
       backdrop and must not get the app chrome. Both paths are also listed in
       `publicPages` below; without that the guard bounces the (by definition
       logged-out) visitor straight back to /login. */
    meta: {
      title: "Forgot password",
      fullScreen: true,
    },
    path: "/forgot-password",
    name: "forgot-password",
    component: () =>
      import(/* webpackChunkName: "login" */ "@/views/ForgotPassword.vue"),
  },
  {
    /* Password reset, step 2 of 2. The token and address arrive as query
       params — and because this is a hash router they live INSIDE the
       fragment, so the view reads `route.query`, never `location.search`. */
    meta: {
      title: "Reset password",
      fullScreen: true,
    },
    path: "/reset-password",
    name: "reset-password",
    component: () =>
      import(/* webpackChunkName: "login" */ "@/views/ResetPassword.vue"),
  },
  {
    meta: {
      title: "Error",
      fullScreen: true,
    },
    path: "/error",
    name: "error",
    component: () =>
      import(/* webpackChunkName: "error" */ "@/views/Error.vue"),
  },
  {
    meta: {
      title: "Voting Profiles",
    },
    path: "/advanced/profiles",
    name: "Voting Profiles",
    component: () =>
      import(/* webpackChunkName: "error" */ "@/views/VoteProfile.vue"),
  },
  {
    meta: {
      title: "Proxy Profiles",
    },
    path: "/advanced/profiles/proxy",
    name: "Proxy Profiles",
    component: () =>
      import(/* webpackChunkName: "error" */ "@/views/ProxyProfile.vue"),
  },
  // {
  //   meta: {
  //     title: "Subscriptions",
  //   },
  //   path: "/website-subscriptions",
  //   name: "Subscriptions",
  //   component: () =>
  //     import(/* webpackChunkName: "error" */ "@/views/WebsiteSubscriptions.vue"),
  // },
  {
    meta: {
      title: "Invoices",
    },
    path: "/invoices",
    name: "invoices",
    component: () =>
      import(/* webpackChunkName: "error" */ "@/views/Invoices.vue"),
  },
  {
    meta: {
      title: "Support",
    },
    path: "/support",
    name: "support",
    component: () =>
      import(/* webpackChunkName: "error" */ "@/views/Support.vue"),
  },
  {
    meta: {
      title: "Verify Email",
    },
    path: "/verify",
    name: "verify",
    component: () =>
      import(/* webpackChunkName: "error" */ "@/views/Verify.vue"),
  },
  {
    meta: {
      title: "Timeline",
    },
    path: "/timeline",
    name: "timeline",
    component: () =>
      import(/* webpackChunkName: "error" */ "@/views/Timeline.vue"),
  },
  /*
   * The full notification history. Authenticated by default — `beforeEach`
   * below treats every path that is not in `publicPages` as requiring a session,
   * so this needs no flag of its own and cannot be reached signed out.
   *
   * Deliberately NOT added to menu.js / AsideMenu.vue: it is reached from the
   * bell in the header, which is where you already are when you have a notice to
   * read, and the sidebar is not to grow another entry.
   */
  /*
   * The step-by-step guide with annotated screenshots and the "Ask" box
   * (src/views/Tutorial.vue). `?step=order-2` deep-links to one step.
   */
  {
    meta: {
      title: "Tutorial",
    },
    path: "/tutorial",
    name: "tutorial",
    component: () => import("@/views/Tutorial.vue"),
  },
  {
    meta: {
      title: "Notifications",
    },
    path: "/notifications",
    name: "notifications",
    component: () =>
      import(/* webpackChunkName: "error" */ "@/views/Notifications.vue"),
  },

  /*
   * Catch-all. There was none, so any unmatched path rendered the shell with an
   * empty <router-view> — a blank page rather than an answer. That was mostly
   * unreachable while the admin template's demo routes existed to absorb stray
   * URLs; deleting them made it easy to hit. A stale link now lands somewhere
   * real. Must stay last: routes match in order.
   */
  { path: "/:pathMatch(.*)*", redirect: "/dashboard" },

];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    /* /tutorial scrolls itself: `?step=` is written as the reader jumps between
       steps and read on arrival to deep-link one (Tutorial.vue). Resetting to
       the top here undid every jump the moment the query changed. */
    if (to.name === "tutorial" && (from.name === "tutorial" || to.query.step)) return false;
    return savedPosition || { top: 0 };
  },
});

router.beforeEach((to, from, next) => {

  const text = {
    displayText: null,
    displayType: null,
    displayHeader: null,
  };
  store.dispatch("setDisplayMessage", text);

  /* Reachable without a session. The two password-reset paths MUST be here:
     anyone following a reset link is by definition signed out, so leaving them
     off sends every one of them to /login and the feature never works.
     Matching is on `to.path`, which excludes the query — so the token on
     /reset-password?token=… does not affect the lookup. */
  const publicPages = [
    "/login",
    "/register",
    "/home",
    "/landing",
    "/auth/callback",
    "/wave-test",
    "/forgot-password",
    "/reset-password",
  ];
  const authRequired = !publicPages.includes(to.path);
  const loggedIn = localStorage.getItem("user");

  if (authRequired) {
    store.dispatch("auth/isExpired");
  }

  // if (!to.path.includes("profile") && !to.path.includes("verify") && !store?.state?.auth?.user?.user?.email_verified_at) {
  //   next('/verify')
  // }

  // if (loggedIn) {
  //   authService.getUser()
  // }

  // trying to access a restricted page + not logged in
  // redirect to login page
  if (authRequired && !loggedIn) {
    next("/login");
  } else {

    /**
     * Fingerprinting
     */
    if (
      store.state &&
      store.state.auth &&
      store.state.auth.user &&
      store.state.auth.user.user
    ) {
      const fingerprintUser = store?.state?.auth?.user?.user?.fingerprint;
      //const scrcl = store?.state?.auth?.user?.user?.scrcl;
      const ip = store?.state?.auth?.user?.user?.ip;

      /* Telemetry, so every failure is swallowed. ipify is on most blocklists:
         without the catch, a blocked request became an unhandled rejection on
         every navigation, and main.js turns those into the "Something went
         wrong on this page" toast. */
      if (!ip || ip.length <= 0) {
        fetch('https://api.ipify.org/?format=json')
          .then(response => response.json())
          .then(data => authService.setIp(data.ip))
          .then(() => authService.getUser())
          .catch(() => {});
      }

      /**
       * if (!scrcl || scrcl.length <= 0) {
        const fpd = fingerprint_display()

        authService.setScrcl(fpd).then(() => {
          authService.getUser();
        })
      }
       */
      

      if (!fingerprintUser || fingerprintUser.length <= 0) {
        FingerprintJS.load()
          .then((fp) => fp.get())
          .then((result) => authService.setFingerprint(result.visitorId))
          .then(() => authService.getUser())
          .catch(() => {});
      }
    }

    next();
  }
});

export default router;
